import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { disabled, form, FormField, minLength, required, requiredError } from '@angular/forms/signals';
import { endOfDay, startOfDay } from 'date-fns';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { ServiceGroupEnum } from '@libs/types/src/services/enum/service-group.enum';
import {
    SocialServiceOfferObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-offer.object';
import {
    SocialServiceOfferGroupObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-offer-group.object';
import { ServiceGroupUpsertOffersDialogStore } from './service-group-upsert-offers-dialog.store';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input-v2/date-input.component';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input-v2/number-input.component';
import {
    MultiSelectInputComponent,
} from '../../../../../../components/kit/input/multi-select-input-v2/multi-select-input.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import { dateify } from '../../../../../shared/helpers/date.helper';
import { checkOk } from '../../../../content.error';
import {
    ServiceGroupUpsertOffersDialogData, ServiceGroupUpsertOffersForm,
} from './service-group-upsert-offers-dialog.types';
import { serviceGroupOfferOptionsFragment } from './service-group-upsert-offers-dialog.query';

@Component({
    selector: 'app-service-groups-upsert-offers-dialog',
    templateUrl: 'service-group-upsert-offers-dialog.component.html',
    styleUrl: 'service-group-upsert-offers-dialog.component.scss',
    providers: [ServiceGroupUpsertOffersDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormField,
        DateInputComponent,
        MultiSelectInputComponent,
        NumberInputComponent,
        ActionButtonsComponent,
    ],
})
export class ServiceGroupUpsertOffersDialogComponent extends BaseDialogComponent {
    public readonly store = inject(ServiceGroupUpsertOffersDialogStore);

    protected readonly data = this.dialogConfig.data as ServiceGroupUpsertOffersDialogData;
    protected readonly hasCredits = this.data.groupType === ServiceGroupEnum.CREDITS;
    protected readonly model = signal<ServiceGroupUpsertOffersForm>({
        validFrom: this.data.item ? dateify(this.data.item.validFrom) : new Date(),
        validTo: dateify(this.data.item?.validTo),
        offerIds: this.data.item ? [this.data.item.offerId] : [],
        creditPrice: this.data.item?.creditPrice ?? null,
    });
    protected readonly fields = form(this.model, schema => {
        disabled(schema, () => this.store.loadingAny());
        required(schema.offerIds);
        minLength(schema.offerIds, 1, { error: requiredError() });
        if (this.hasCredits) required(schema.creditPrice);
    });
    protected readonly offers = signal<InfiniteScrollStore<SocialServiceOfferObject> | null>(null);

    constructor() {
        super();
        if (!this.data.item) void this.loadOffers();
    }

    public override async save(): Promise<void> {
        if (this.store.loadingAny() || this.fields().invalid() || !this.data.groupId) return;
        const value = this.model();
        const data = {
            validFrom: value.validFrom ? startOfDay(value.validFrom).toISOString() : null,
            validTo: value.validTo ? endOfDay(value.validTo).toISOString() : null,
            creditPrice: this.hasCredits ? value.creditPrice : undefined,
        };
        const result = this.data.item
            ? await this.store.api.updateSocialServiceOfferGroup({ id: this.data.item.id, ...data })
            : await this.store.api.addOffersToSocialServiceOfferGroup({
                groupId: this.data.groupId,
                offerIds: value.offerIds,
                ...data,
            });
        if (checkOk(result, this.data.item ? 'update' : 'create')) this.dialogRef.close({ refresh: true });
    }

    private async loadOffers(): Promise<void> {
        const groupFilter = new FilterBuilder({ groupId: this.data.groupId }).validAt(new Date()).limit(10000);
        const assigned = await this.store.api.getSocialServiceOfferGroups(groupFilter.filter(), 'offerId');
        const filter = new FilterBuilder({ serviceId: this.data.serviceId }).limit(100)
            .where('id', assigned.items.map((item: SocialServiceOfferGroupObject) => item.offerId), 'NIN');
        this.offers.set(new InfiniteScrollStore<SocialServiceOfferObject>(
            this.store.api.getSocialServiceOffers,
            {
                ...filter.filter(),
                additionalFilter: this.data.additionalFilter,
            },
            true,
            serviceGroupOfferOptionsFragment,
        ));
    }
}
